import * as signalR from "@microsoft/signalr";
import { apiBaseUrl } from "../config";

const { HubConnectionState } = signalR;

// Delays (ms) between automatic reconnect attempts after an established connection drops.
// The last value repeats forever, so the client never gives up on its own.
const RECONNECT_DELAYS_MS = [0, 2000, 5000, 10000, 20000, 30000];
// Backoff for retrying the initial connection while the server cannot be reached at all.
const INITIAL_RETRY_BASE_MS = 1000;
const INITIAL_RETRY_MAX_MS = 30000;
const PING_TIMEOUT_MS = 5000;
// A tab hidden at least this long has almost certainly been dropped by the server (it times
// idle clients out after 60 s), so reconnect straight away instead of pinging first.
const STALE_AFTER_HIDDEN_MS = 45000;
const RESUME_THROTTLE_MS = 2000;

export const ConnectionState = Object.freeze({
  Connecting: "connecting",
  Connected: "connected",
  Reconnecting: "reconnecting",
  Disconnected: "disconnected",
});

/** Thrown when a connection attempt cannot start because one is already in progress. */
export class ConnectionBusyError extends Error {
  expected = true;
}

// A single SignalR connection shared by the whole app. Handlers registered with `on` survive
// reconnects; group membership does not, so the app re-joins its session whenever the
// connection comes back (see AppInit).
const connection = new signalR.HubConnectionBuilder()
  .withUrl(`${apiBaseUrl}/hubs`)
  .withAutomaticReconnect({
    nextRetryDelayInMilliseconds: ({ previousRetryCount }) =>
      RECONNECT_DELAYS_MS[Math.min(previousRetryCount, RECONNECT_DELAYS_MS.length - 1)],
  })
  .withServerTimeout(60000)
  .withKeepAliveInterval(15000)
  .configureLogging(signalR.LogLevel.Warning)
  .build();

let wanted = false; // start() was called and stop() was not: keep (re)connecting
let starting = null; // in-flight start() promise, shared by concurrent callers
let retryTimer = null;
let retryAttempt = 0;
let lastResumeAt = 0;
const listeners = new Set();

const emit = (state) => listeners.forEach((listener) => listener(state));

connection.onreconnecting(() => emit(ConnectionState.Reconnecting));
connection.onreconnected(() => {
  retryAttempt = 0;
  emit(ConnectionState.Connected);
});
connection.onclose(() => {
  emit(ConnectionState.Disconnected);
  if (wanted) scheduleRetry();
});

export const isConnected = () => connection.state === HubConnectionState.Connected;

/** Reports every state change (a ConnectionState value); returns a function that removes the listener. */
export function onStateChange(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Connects, and keeps retrying with backoff until connected or stop() is called. */
export function start() {
  wanted = true;
  return connectNow();
}

export async function stop() {
  wanted = false;
  clearRetry();
  await connection.stop();
}

/** True when the server answers a ping within the timeout; false for a dead or very slow link. */
export async function ping(timeoutMs = PING_TIMEOUT_MS) {
  if (!isConnected()) return false;
  let timer;
  try {
    await Promise.race([
      connection.invoke("Ping"),
      new Promise((resolve, reject) => {
        timer = setTimeout(() => reject(new Error("Ping timed out")), timeoutMs);
      }),
    ]);
    return true;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Called when the page comes back to the foreground or the network returns: verifies the
 * connection, or reconnects immediately rather than waiting for a pending retry timer.
 * Resolves true when connected afterwards.
 */
export async function resume({ hiddenMs = 0 } = {}) {
  if (!wanted) return false;
  const now = Date.now();
  if (now - lastResumeAt < RESUME_THROTTLE_MS) return isConnected();
  lastResumeAt = now;

  if (isConnected()) {
    if (hiddenMs < STALE_AFTER_HIDDEN_MS && (await ping())) return true;
    await connection.stop().catch(() => {}); // dead socket; onclose reports "disconnected"
  } else if (connection.state !== HubConnectionState.Disconnected && !starting) {
    await connection.stop().catch(() => {}); // abort a sleeping automatic-reconnect timer
  }

  try {
    await connectNow();
    return true;
  } catch {
    return false;
  }
}

export function invoke(method, ...args) {
  if (!isConnected()) {
    return Promise.reject(new Error("Not connected to the server"));
  }
  return connection.invoke(method, ...args);
}

export function on(method, handler) {
  connection.on(method, handler);
  return () => connection.off(method, handler);
}

function connectNow() {
  clearRetry();
  if (connection.state === HubConnectionState.Connected) return Promise.resolve();
  if (starting) return starting;
  if (connection.state !== HubConnectionState.Disconnected) {
    return Promise.reject(new ConnectionBusyError(`Connection is ${connection.state}`));
  }

  emit(ConnectionState.Connecting);
  starting = connection
    .start()
    .then(() => {
      retryAttempt = 0;
      emit(ConnectionState.Connected);
    })
    .catch((error) => {
      emit(ConnectionState.Disconnected);
      if (wanted) scheduleRetry();
      throw error;
    })
    .finally(() => {
      starting = null;
    });
  return starting;
}

function scheduleRetry() {
  clearRetry();
  const delay =
    Math.min(INITIAL_RETRY_MAX_MS, INITIAL_RETRY_BASE_MS * 2 ** retryAttempt) + Math.random() * 500;
  retryAttempt += 1;
  retryTimer = setTimeout(() => {
    retryTimer = null;
    if (wanted) connectNow().catch(() => {});
  }, delay);
}

function clearRetry() {
  if (retryTimer) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
}
